import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Trash2, RotateCcw, Search, Database,
  CheckCircle2, AlertTriangle, Key, Layers, ArrowUpDown, Info
} from 'lucide-react';

export interface TableColumnDef {
  name: string;
  type: string;
  is_primary?: boolean;
  is_indexed?: boolean;
  description?: string;
}

export const SQL_DATA_TYPES = [
  { value: 'real', label: 'real (Float 32-bit, Default for Telemetry)', category: 'numeric', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'float', label: 'float (Float 64-bit Double)', category: 'numeric', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'decimal(18,2)', label: 'decimal(18,2) (High Precision)', category: 'numeric', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'int', label: 'int (32-bit Integer)', category: 'numeric', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'bigint', label: 'bigint (64-bit Integer)', category: 'numeric', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'varchar(50)', label: 'varchar(50) (Short Text / ID)', category: 'text', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'varchar(100)', label: 'varchar(100) (Name / Label)', category: 'text', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'varchar(255)', label: 'varchar(255) (Long Text)', category: 'text', color: 'text-[#1C4D88] bg-[#F2F8FC] border-[#C2DFF1]' },
  { value: 'datetime', label: 'datetime (Date & Time)', category: 'datetime', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'date', label: 'date (Date only)', category: 'datetime', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'time(7)', label: 'time(7) (Time only)', category: 'datetime', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'bit', label: 'bit (Boolean Flag 0/1)', category: 'boolean', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' }
];

export const DEFAULT_LIVEDAYCONS_COLUMNS: TableColumnDef[] = [
  { name: 'company_id', type: 'int', description: 'Enterprise Company Foreign Key' },
  { name: 'site_code', type: 'varchar(50)', description: 'Universal Unique Site Code' },
  { name: 'Date', type: 'date', description: 'Calendar Date' },
  { name: 'Time', type: 'time(7)', description: 'Sample Time Interval' },
  { name: 'Datetime', type: 'datetime', is_indexed: true, description: 'High-speed Clustered Index Key' },
  { name: 'Hour', type: 'int', is_indexed: true, description: 'Hour of Day (0-23)' },
  { name: 'Minute', type: 'int', is_indexed: true, description: 'Minute Interval (0-59)' },
  { name: 'PowerPlant_Name', type: 'varchar(100)', description: 'Plant Display Descriptor' },
  { name: 'Plant_Actual_DCCapacity_MWp', type: 'real', description: 'Installed DC Peak Capacity' },
  { name: 'Plant_PVSyst_DCCapacity_MWp', type: 'real', description: 'PVSyst Projected DC Capacity' },
  { name: 'Plant_Tarrif', type: 'real', description: 'PPA Tariff Rate' },
  { name: 'Plant_Actual_ACCapacity_MW', type: 'real', description: 'Active AC Grid Export Capacity' },
  { name: 'Plant_ActivePower_MW', type: 'real', description: 'Instantaneous Active Power (MW)' },
  { name: 'Plant_PeakActivePower_MW', type: 'real', description: 'Daily Peak Active Power (MW)' },
  { name: 'Plant_ExportEnergy_MWh', type: 'real', description: 'Cumulative Export Energy (MWh)' },
  { name: 'Plant_ImportEnergy_MWh', type: 'real', description: 'Grid Import Energy (MWh)' },
  { name: 'MCR_GIR_Wpm2', type: 'real', description: 'Master Control Room Global Irradiance' },
  { name: 'Block1_GIR_Wpm2', type: 'real', description: 'Block 1 Solar Pyranometer Irradiance' },
  { name: 'Block2_GIR_Wpm2', type: 'real', description: 'Block 2 Solar Pyranometer Irradiance' },
  { name: 'Avg_GIR_Wpm2', type: 'real', description: 'Field Averaged Global Irradiance' },
  { name: 'GHI_Wpm2', type: 'real', description: 'Global Horizontal Irradiance' },
  { name: 'Block1_GTI_Wpm2', type: 'real', description: 'Global Tilted Irradiance Block 1' },
  { name: 'Block2_GTI_Wpm2', type: 'real', description: 'Global Tilted Irradiance Block 2' },
  { name: 'Avg_GTI_Wpm2', type: 'real', description: 'Field Averaged Global Tilted Irradiance' },
  { name: 'MCR_Tamb_DegC', type: 'real', description: 'Weather Ambient Temperature' },
  { name: 'Avg_Tamb_DegC', type: 'real', description: 'Field Averaged Ambient Temp' },
  { name: 'Avg_ModTemp_DegC', type: 'real', description: 'Solar PV Module Temperature' },
  { name: 'MCR_WindSpeed_mps', type: 'real', description: 'Anemometer Wind Speed (m/s)' },
  { name: 'MCR_Humidity_Pct', type: 'real', description: 'Relative Atmospheric Humidity %' },
  { name: 'MCR_Rain_mm', type: 'real', description: 'Rain Gauge Precipitation (mm)' },
  { name: 'Bus1_Frequency_Hz', type: 'real', description: 'Grid Frequency (Hz)' },
  { name: 'Bus1_R_Y_LineVoltage_KV', type: 'real', description: 'R-Y High Voltage Line (kV)' },
  { name: 'Bus1_Y_B_LineVoltage_KV', type: 'real', description: 'Y-B High Voltage Line (kV)' },
  { name: 'Bus1_B_R_LineVoltage_KV', type: 'real', description: 'B-R High Voltage Line (kV)' },
  { name: 'IPR', type: 'real', description: 'Instantaneous Performance Ratio' },
  { name: 'Expected_Energy_MWh', type: 'real', description: 'Model Projected Generation' },
  { name: 'Expected_Energy_PVSyst_MWh', type: 'real', description: 'PVSyst Projected Generation' },
  { name: 'Plant_CUF', type: 'real', description: 'Capacity Utilization Factor' },
  { name: 'Plant_PLF', type: 'real', description: 'Plant Load Factor' },
  { name: 'Plant_Performance_Ratio', type: 'real', description: 'Cumulative PR %' },
  { name: 'Plant_Equivalent_Sun_Hours', type: 'real', description: 'Equivalent Sun Hours (ESH)' },
  { name: 'Created_Date', type: 'datetime', description: 'System Ingestion Timestamp' }
];

export const DEFAULT_LIVESTRINGINV_COLUMNS: TableColumnDef[] = [
  { name: 'company_id', type: 'int', description: 'Enterprise Company Foreign Key' },
  { name: 'site_code', type: 'varchar(50)', description: 'Universal Unique Site Code' },
  { name: 'Date', type: 'date', description: 'Calendar Date' },
  { name: 'Time', type: 'time(7)', description: 'Sample Time Interval' },
  { name: 'Datetime', type: 'datetime', is_indexed: true, description: 'High-speed Clustered Index Key' },
  { name: 'Hour', type: 'int', is_indexed: true, description: 'Hour of Day (0-23)' },
  { name: 'Minute', type: 'int', is_indexed: true, description: 'Minute Interval (0-59)' },
  { name: 'Block', type: 'varchar(50)', description: 'Solar Array Sector / Block' },
  { name: 'ICR', type: 'varchar(50)', description: 'Inverter Control Room identifier' },
  { name: 'Inv_ShortName', type: 'varchar(50)', description: 'Short Inverter Tag (INV1..)' },
  { name: 'Inv', type: 'varchar(50)', description: 'Inverter Identifier' },
  { name: 'Inverter', type: 'varchar(50)', description: 'Canonical Inverter Visual Key' },
  { name: 'Inv_DCLoad_MWp', type: 'real', description: 'Connected DC Load (MWp)' },
  { name: 'Inv_Energy_KWh', type: 'real', description: 'Daily Interval Energy Generated (kWh)' },
  { name: 'Inv_TotalEnergy_KWh', type: 'real', description: 'Lifetime Cumulative Meter Energy (kWh)' },
  { name: 'Inv_DCVoltage_V', type: 'real', description: 'Inverter Bus DC Voltage (V)' },
  { name: 'Inv_DCCurrent_A', type: 'real', description: 'Inverter Bus DC Current (A)' },
  { name: 'Inv_DCPower_KW', type: 'real', description: 'Instantaneous DC Power Input (kW)' },
  { name: 'Inv_ACVoltage_V', type: 'real', description: 'Inverter AC Grid Voltage (V)' },
  { name: 'Inv_ActivePower_KW', type: 'real', description: 'Instantaneous AC Power Output (kW)' },
  { name: 'Inv_Efficiency_Pct', type: 'real', description: 'Inverter Conversion Efficiency %' },
  { name: 'Inv_IGBTTemp_DegC', type: 'real', description: 'IGBT Transistor Internal Temp' },
  { name: 'Block_GIR_Wpm2', type: 'real', description: 'Block Specific Irradiance' },
  { name: 'Block_Tamb_DegC', type: 'real', description: 'Block Ambient Temp' },
  { name: 'Block_ModTemp_DegC', type: 'real', description: 'Block Module Surface Temp' },
  { name: 'IPR', type: 'real', description: 'Inverter Instantaneous PR' },
  { name: 'Expected_Energy_KWh', type: 'real', description: 'Inverter Projected Generation' },
  { name: 'Inv_CUF', type: 'real', description: 'Inverter Capacity Utilization Factor' },
  { name: 'Inv_PLF', type: 'real', description: 'Inverter Plant Load Factor' },
  { name: 'Inv_Performance_Ratio', type: 'real', description: 'Inverter PR %' },
  { name: 'Inv_Equivalent_Sun_Hours', type: 'real', description: 'Inverter Sun Hours' },
  { name: 'Created_Date', type: 'datetime', description: 'System Ingestion Timestamp' }
];

interface TableSchemaModalProps {
  isOpen: boolean;
  tableType: 'livedaycons' | 'livestringinv' | null;
  initialColumns: TableColumnDef[];
  onSave: (updatedColumns: TableColumnDef[]) => void;
  onClose: () => void;
}

export const TableSchemaModal: React.FC<TableSchemaModalProps> = ({
  isOpen,
  tableType,
  initialColumns,
  onSave,
  onClose
}) => {
  const [columns, setColumns] = useState<TableColumnDef[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newColName, setNewColName] = useState('');
  const [newColType, setNewColType] = useState('real');
  const [newColDesc, setNewColDesc] = useState('');
  const [showAddRow, setShowAddRow] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Sync columns with initialColumns when modal opens
  useEffect(() => {
    if (isOpen) {
      setColumns(initialColumns && initialColumns.length > 0 
        ? JSON.parse(JSON.stringify(initialColumns)) 
        : (tableType === 'livedaycons' ? DEFAULT_LIVEDAYCONS_COLUMNS : DEFAULT_LIVESTRINGINV_COLUMNS)
      );
      setSearchQuery('');
      setShowAddRow(false);
      setErrorMsg(null);
    }
  }, [isOpen, initialColumns, tableType]);

  const tableName = tableType === 'livedaycons'
    ? 'dbo.axonmetric_livedaycons'
    : 'dbo.axonmetric_livestringinv';

  const tableTitle = tableType === 'livedaycons'
    ? 'Plant Live Day Consolidation Table'
    : 'String Inverter Live Telemetry Table';

  const tableDescription = tableType === 'livedaycons'
    ? 'Stores plant-level power, weather radiation (GIR/GTI/GHI), grid export/import energy, and site KPIs at 5-minute intervals.'
    : 'Stores string inverter telemetry including DC voltage, DC current, AC active power, efficiency, and inverter PR.';

  // Filter columns based on search
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return columns;
    const q = searchQuery.toLowerCase().trim();
    return columns.filter(c => 
      c.name.toLowerCase().includes(q) || 
      c.type.toLowerCase().includes(q) ||
      (c.description && c.description.toLowerCase().includes(q))
    );
  }, [columns, searchQuery]);

  // Handle column name change
  const handleNameChange = (index: number, newName: string) => {
    setErrorMsg(null);
    // Sanitize: letters, numbers, underscores only
    const sanitized = newName.replace(/[^a-zA-Z0-9_]/g, '');
    const next = [...columns];
    next[index] = { ...next[index], name: sanitized };
    setColumns(next);
  };

  // Handle data type change
  const handleTypeChange = (index: number, newType: string) => {
    const next = [...columns];
    next[index] = { ...next[index], type: newType };
    setColumns(next);
  };

  // Handle column deletion
  const handleDeleteColumn = (index: number) => {
    const colToDelete = columns[index];
    if (colToDelete.name.toLowerCase() === 'datetime') {
      setErrorMsg("'Datetime' is required for database indexing and clustering. It cannot be removed.");
      return;
    }
    const next = [...columns];
    next.splice(index, 1);
    setColumns(next);
    setErrorMsg(null);
  };

  // Add new column
  const handleAddNewColumn = () => {
    if (!newColName.trim()) {
      setErrorMsg('Please specify a column name');
      return;
    }
    const sanitized = newColName.trim().replace(/[^a-zA-Z0-9_]/g, '');
    if (!sanitized) {
      setErrorMsg('Column name contains invalid characters. Use alphanumeric characters and underscores only.');
      return;
    }
    if (columns.some(c => c.name.toLowerCase() === sanitized.toLowerCase())) {
      setErrorMsg(`Column '${sanitized}' already exists in this table.`);
      return;
    }

    const newCol: TableColumnDef = {
      name: sanitized,
      type: newColType,
      description: newColDesc.trim() || 'Custom Added Column'
    };

    setColumns([newCol, ...columns]);
    setNewColName('');
    setNewColDesc('');
    setShowAddRow(false);
    setErrorMsg(null);
  };

  // Reset to default sample
  const handleResetToDefault = () => {
    if (window.confirm('Reset this table schema to the standard enterprise sample columns?')) {
      const defaults = tableType === 'livedaycons' 
        ? DEFAULT_LIVEDAYCONS_COLUMNS 
        : DEFAULT_LIVESTRINGINV_COLUMNS;
      setColumns(JSON.parse(JSON.stringify(defaults)));
      setErrorMsg(null);
    }
  };

  // Save changes
  const handleSave = () => {
    // Validate that Datetime column exists
    const hasDatetime = columns.some(c => c.name.toLowerCase() === 'datetime');
    if (!hasDatetime) {
      setErrorMsg("A 'Datetime' column is required for clustered indexing and time-series queries.");
      return;
    }

    // Check for duplicate names
    const names = columns.map(c => c.name.toLowerCase());
    const duplicates = names.filter((item, index) => names.indexOf(item) !== index);
    if (duplicates.length > 0) {
      setErrorMsg(`Duplicate column names detected: ${duplicates.join(', ')}. Each column must have a unique name.`);
      return;
    }

    onSave(columns);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col max-h-[90vh] overflow-hidden font-sans"
        >
          {/* Header */}
          <div className="px-6 py-5 bg-gradient-to-r from-slate-900 via-slate-800 to-[#0D2747] text-white flex items-center justify-between border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0084CF]/20 border border-[#3EA3DA]/30 flex items-center justify-center text-[#3EA3DA] shadow-inner">
                <Database size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold tracking-tight text-white">
                    {tableTitle}
                  </h3>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-[#0084CF]/20 text-[#8AC4E6] border border-[#3EA3DA]/30">
                    {tableName}
                  </span>
                </div>
                <p className="text-xs text-slate-300 mt-0.5 max-w-2xl">
                  {tableDescription}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Controls Bar */}
          <div className="px-6 py-3.5 bg-slate-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search column name, datatype, or description..."
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:outline-hidden"
                />
              </div>
              <span className="text-xs font-semibold text-gray-500 shrink-0">
                {filteredColumns.length} of {columns.length} columns
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setShowAddRow(!showAddRow)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#0066A3] text-white hover:bg-[#1C4D88] shadow-xs transition-colors cursor-pointer"
              >
                <Plus size={14} />
                Add Column
              </button>
              <button
                type="button"
                onClick={handleResetToDefault}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                title="Reset back to platform default sample columns" aria-label="Reset back to platform default sample columns"
              >
                <RotateCcw size={14} />
                Reset Sample
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {errorMsg && (
            <div className="mx-6 mt-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-xs text-red-700">
              <AlertTriangle size={16} className="text-red-500 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Add Column Drawer / Inline Form */}
          {showAddRow && (
            <div className="mx-6 mt-3 p-3.5 bg-[#F2F8FC]/70 border border-[#C2DFF1] rounded-xl space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#0D2747] flex items-center gap-1.5">
                  <Plus size={14} className="text-[#0066A3]" />
                  Define New Column
                </span>
                <button
                  type="button"
                  onClick={() => setShowAddRow(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Column Name *</label>
                  <input
                    type="text"
                    value={newColName}
                    onChange={(e) => setNewColName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    placeholder="e.g. Inv_CoolingFan_RPM"
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-[#8AC4E6] rounded-lg focus:ring-2 focus:ring-[#0084CF] font-mono text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Data Type *</label>
                  <select
                    value={newColType}
                    onChange={(e) => setNewColType(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-[#8AC4E6] rounded-lg focus:ring-2 focus:ring-[#0084CF] font-mono text-gray-900"
                  >
                    {SQL_DATA_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Description (Optional)</label>
                  <input
                    type="text"
                    value={newColDesc}
                    onChange={(e) => setNewColDesc(e.target.value)}
                    placeholder="Brief telemetry context"
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-[#8AC4E6] rounded-lg focus:ring-2 focus:ring-[#0084CF] text-gray-900"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={handleAddNewColumn}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-[#0066A3] text-white hover:bg-[#1C4D88] shadow-xs transition-all cursor-pointer"
                >
                  Insert Column Into Schema
                </button>
              </div>
            </div>
          )}

          {/* Table Schema Body */}
          <div className="p-6 overflow-y-auto flex-1 divide-y divide-gray-100">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/70 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    <th className="py-2.5 px-3 w-12 text-center">#</th>
                    <th className="py-2.5 px-3">Column Name</th>
                    <th className="py-2.5 px-3 w-64">SQL Data Type</th>
                    <th className="py-2.5 px-3">Purpose / Notes</th>
                    <th className="py-2.5 px-3 w-28 text-center">Index / Key</th>
                    <th className="py-2.5 px-3 w-16 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs">
                  {filteredColumns.map((col, idx) => {
                    const originalIndex = columns.findIndex(c => c.name === col.name);
                    const isDatetime = col.name.toLowerCase() === 'datetime';
                    const isKeyIndex = isDatetime || col.name.toLowerCase() === 'hour' || col.name.toLowerCase() === 'minute';

                    return (
                      <tr key={idx} className="hover:bg-[#F2F8FC]/30 transition-colors group">
                        <td className="py-2 px-3 text-center text-gray-400 font-mono text-[11px]">
                          {originalIndex + 1}
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={col.name}
                            disabled={isDatetime}
                            onChange={(e) => handleNameChange(originalIndex, e.target.value)}
                            className={`w-full px-2 py-1 font-mono text-xs rounded-md border ${
                              isDatetime 
                                ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed font-bold' 
                                : 'bg-white text-gray-900 border-gray-200 focus:border-[#0084CF] focus:ring-1 focus:ring-[#0084CF] group-hover:border-gray-300'
                            }`}
                            placeholder="column_name"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <select
                            value={col.type.toLowerCase()}
                            disabled={isDatetime}
                            onChange={(e) => handleTypeChange(originalIndex, e.target.value)}
                            className={`w-full px-2 py-1 font-mono text-xs rounded-md border ${
                              isDatetime
                                ? 'bg-slate-100 text-slate-700 border-slate-200 cursor-not-allowed'
                                : 'bg-white text-gray-800 border-gray-200 focus:border-[#0084CF] focus:ring-1 focus:ring-[#0084CF]'
                            }`}
                          >
                            {SQL_DATA_TYPES.map(t => (
                              <option key={t.value} value={t.value}>
                                {t.value}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3 text-gray-500 text-[11px] truncate max-w-xs">
                          {col.description || 'Custom Telemetry Metric'}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {isDatetime ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                              <Key size={10} /> Clustered
                            </span>
                          ) : isKeyIndex ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#E3F0F9] text-[#1C4D88] border border-[#C2DFF1]">
                              <Layers size={10} /> Indexed
                            </span>
                          ) : (
                            <span className="text-gray-300 text-[11px]">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            type="button"
                            disabled={isDatetime}
                            onClick={() => handleDeleteColumn(originalIndex)}
                            className={`p-1 rounded-md transition-colors ${
                              isDatetime
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer'
                            }`}
                            title={isDatetime ? "Required for clustered indexing" : "Remove column"}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredColumns.length === 0 && (
              <div className="py-12 text-center text-gray-400">
                <Search size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">No columns match "{searchQuery}"</p>
                <p className="text-xs text-gray-400 mt-1">Clear the search query or add a new custom column.</p>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 bg-slate-50 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Info size={15} className="text-[#0066A3]" />
              <span>
                These exact columns and types will be executed as DDL on database creation.
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold rounded-lg bg-[#0066A3] hover:bg-[#1C4D88] text-white shadow-md transition-all cursor-pointer"
              >
                <CheckCircle2 size={15} />
                Save & Apply Schema ({columns.length} Columns)
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
